import React from 'react';

import { Alert, Center, Divider, LoadingOverlay, Stack, useMantineTheme } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import AthleteAppointmentStateStack from 'components/AthleteAppointment/AthleteAppointmentStateStack';
import { useAuth } from 'context/auth-context';
import useAppointmentAthleteRetrieve from 'hooks/appointment/useAppointmentAthleteRetrieve';
import { useAppointmentMeta } from 'hooks/appointment/useAppointmentMeta';
import useAthleteAppointmentMeta from 'hooks/athleteAppointment/useAthleteAppointmentMeta';
import useAthleteAppointmentState from 'hooks/athleteAppointment/useAthleteAppointmentState';
import useGymAthleteRetrieve from 'hooks/gym/useGymAthleteRetrieve';
import BackHeader from 'layouts/MobileLayout/components/BackHeader';

import AppointmentCancelSection from './Appointment/AppointmentCancelSection';
import AppointmentMainSection from './Appointment/AppointmentMainSection';
import AthleteListSection from './shared/AthleteListSection';
import HeaderSection from './shared/HeaderSection';

interface AppointmentDetailProps {
  eventId: string;
}

const AppointmentDetail: React.FC<AppointmentDetailProps> = ({ eventId }) => {
  const theme = useMantineTheme();
  const { session } = useAuth();
  const { data: gymData } = useGymAthleteRetrieve(session?.gym?.gymId);
  const hideReservedAthletes = gymData?.data?.athleteAppHideReservedAthletes ?? false;

  const {
    data: appointmentData,
    isLoading: isAppointmentDataLoading,
    isError: isAppointmentError,
  } = useAppointmentAthleteRetrieve(eventId);

  const athleteAppointment = appointmentData?.data.athleteAppointments.find(
    aa => aa.athlete?.athleteId === session?.athlete?.athleteId,
  );

  const appointmentMeta = useAppointmentMeta(appointmentData?.data);
  const athleteAppointmentState = useAthleteAppointmentState(athleteAppointment);
  const athleteAppointmentMeta = useAthleteAppointmentMeta(athleteAppointmentState, appointmentMeta);

  if (isAppointmentError) {
    return (
      <>
        <BackHeader title='Appointment Details' />
        <Center sx={{ flexGrow: 1 }} p='md'>
          <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
            Something went wrong while fetching appointment data.
          </Alert>
        </Center>
      </>
    );
  }

  if (!appointmentData?.data) {
    return (
      <>
        <BackHeader title='Appointment Details' />
        <LoadingOverlay visible={isAppointmentDataLoading} />
      </>
    );
  }

  const { data: appointmentInfo } = appointmentData;

  const eventCoachRoles = appointmentInfo.appointmentCoachAppointmentCoachRoles.map(acacr => ({
    coach: acacr.coach,
    role: acacr.appointmentCoachRole.name,
  }));

  return (
    <>
      <BackHeader title={`${appointmentInfo.appointmentType.name} Details`} />

      <Stack className='content'>
        <LoadingOverlay visible={isAppointmentDataLoading} />

        <HeaderSection
          fullStartDate={appointmentMeta.eventStartDateTime}
          duration={appointmentInfo.duration}
          eventCoachEventCoachRoles={eventCoachRoles}
        />

        <AthleteAppointmentStateStack athleteAppointmentMeta={athleteAppointmentMeta} size='lg' />

        <Divider mx='-md' />

        <AppointmentMainSection appointmentData={appointmentInfo} />

        <Divider mx='-md' />

        {!hideReservedAthletes && (
          <AthleteListSection athleteAppointments={appointmentInfo.athleteAppointments} />
        )}

        {athleteAppointmentMeta.cancelAction && athleteAppointment?.athleteAppointmentId && (
          <AppointmentCancelSection
            appointmentData={appointmentData.data}
            athleteAppointmentId={athleteAppointment.athleteAppointmentId}
            action={athleteAppointmentMeta.cancelAction}
          />
        )}
      </Stack>
    </>
  );
};

export default AppointmentDetail;
